import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  ArrowLeft,
  Upload,
  FileText,
  Eye,
  Download,
  Heart,
  KeyRound,
  Hash,
  Lock,
  Image,
  File,
  FileSpreadsheet,
} from 'lucide-react';
import toast from 'react-hot-toast';

import PageHeader from '../../components/layout/PageHeader';
import Badge from '../../components/ui/Badge';
import DocumentPreviewModal, {
  type PreviewContentKind,
} from '../../components/ui/DocumentPreviewModal';
import caseService from '../../services/case.service';
import contentManagementService from '../../services/contentManagement.service';
import { accessRequestService } from '../../services/accessRequest.service';
import documentService from '../../services/document.service';
import { useDebounce } from '../../hooks/useDebounce';
import { useAuthStore } from '../../store/authStore';
import { ROUTES } from '../../config/routes.config';
import {
  canDownloadDocument,
  canPreviewDocument,
} from '../../utils/documentAccessMode';
import { formatDocumentDisplayName } from '../../utils/documentName';
import HashtagInput from '../../components/features/documents/HashtagInput';

type FolderTab = 'all' | 'Case Files' | 'Correspondence';

interface CaseDocument {
  document_id: number | string;
  original_file_name: string;
  file_extension?: string | null;
  mime_type?: string | null;
  file_size_bytes?: number | null;
  folder_type: string;
  document_type: string;
  classification: 'Public' | 'Confidential' | string;
  approval_status: string;
  page_count?: number | null;
  uploaded_by_name?: string;
  created_at: string;
  is_favorite?: boolean;
  can_view?: boolean;
  /** When true, backend says this user must request access (confidential, not uploader, not CM). */
  requires_access_request?: boolean;
  /** Reason provided by CM when document is denied (Rejected). */
  denial_reason?: string;
}

interface CaseHeader {
  case_id: string;
  cms_number: string;
  rrms_ref_number?: string | null;
  case_approval_status?: 'Pending' | 'Approved' | string;
  pending_hashtags?: string[] | null;
  rejected_hashtags?: string[] | null;
  hashtag_timeline?: Array<{ tag: string; status: 'Approved' | 'Rejected' | 'Pending' }>;
  fir_number?: string;
  district?: string;
  ps_name?: string;
  case_type?: string;
  case_status?: string;
  cms_case_name?: string | null;
  year?: number;
  /** "Section / Sub-Section" path from the admin filing hierarchy. */
  hierarchy_path?: string | null;
  category?: string | null;
  subject?: string | null;
  file_start_date?: string | null;
  file_end_date?: string | null;
  hashtags?: string[] | null;
}

const PAGE_SIZE = 10;

function formatFileSize(bytes?: number | null): string {
  if (bytes == null || Number.isNaN(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(2)} GB`;
}

const CaseDetailPage: React.FC = () => {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { role } = useAuthStore();

  const [folderTab, setFolderTab] = useState<FolderTab>('all');
  const [hashtagsInput, setHashtagsInput] = useState('');
  const [fileNameSearch, setFileNameSearch] = useState('');
  const [page, setPage] = useState(1);
  const [previewDocId, setPreviewDocId] = useState<number | string | null>(
    null
  );
  const [previewContentKind, setPreviewContentKind] =
    useState<PreviewContentKind | null>(null);
  const [requestDoc, setRequestDoc] = useState<CaseDocument | null>(null);
  const [isCaseHashtagModalOpen, setIsCaseHashtagModalOpen] = useState(false);
  const [caseHashtags, setCaseHashtags] = useState<string[]>([]);
  const [approvedCaseHashtags, setApprovedCaseHashtags] = useState<string[]>([]);
  const [rejectedCaseHashtags, setRejectedCaseHashtags] = useState<string[]>([]);
  const [caseHashtagTimeline, setCaseHashtagTimeline] = useState<
    Array<{ tag: string; status: 'Approved' | 'Rejected' | 'Pending' }>
  >([]);

  const handleBackNavigation = () => {
    const navState = (location.state || {}) as {
      returnTo?: string;
      searchState?: unknown;
    };
    if (navState.returnTo === 'search-documents') {
      navigate(ROUTES.SEARCH, {
        state: { restoreSearchState: navState.searchState || {} },
      });
      return;
    }
    navigate(ROUTES.CASES);
  };

  const debouncedHashtags = useDebounce(hashtagsInput, 400);
  const debouncedFileNameSearch = useDebounce(fileNameSearch, 250);

  useEffect(() => {
    document.title = 'RRMS | Case Detail';
  }, []);

  const hashtagsParam = useMemo(() => {
    const raw = debouncedHashtags
      .split(',')
      .map((t) => t.trim().replace(/^#/, ''))
      .filter(Boolean);
    return raw.length ? raw.join(',') : undefined;
  }, [debouncedHashtags]);

  useEffect(() => {
    setPage(1);
  }, [folderTab, hashtagsParam]);

  const caseQuery = useQuery({
    queryKey: ['case', caseId],
    queryFn: () => caseService.getCaseById(String(caseId)),
    enabled: !!caseId,
    select: (res): CaseHeader => {
      const d = (res.data as any)?.data ?? res.data;
      const c = d?.case ?? d;
      return {
        case_id: String(c.case_id),
        cms_number: c.cms_number ?? '',
        rrms_ref_number: c.rrms_ref_number ?? null,
        case_approval_status: c.case_approval_status ?? 'Approved',
        fir_number: c.fir_number ?? '',
        district: c.district ?? '',
        ps_name: c.ps_name ?? '',
        case_type: c.case_type ?? '',
        case_status: c.case_status ?? '',
        cms_case_name: c.cms_case_name ?? '',
        year: c.year,
        hierarchy_path: c.hierarchy_path ?? null,
        category: c.category ?? null,
        subject: c.subject ?? null,
        file_start_date: c.file_start_date ?? null,
        file_end_date: c.file_end_date ?? null,
        hashtags: Array.isArray(c.hashtags) ? c.hashtags : [],
        pending_hashtags: Array.isArray(c.pending_hashtags)
          ? c.pending_hashtags
          : [],
        rejected_hashtags: Array.isArray(c.rejected_hashtags)
          ? c.rejected_hashtags
          : [],
        hashtag_timeline: Array.isArray(c.hashtag_timeline)
          ? c.hashtag_timeline
          : [],
      };
    },
  });

  const docsQuery = useQuery({
    queryKey: ['case-documents', caseId, folderTab, hashtagsParam, page],
    queryFn: () =>
      contentManagementService.getCaseDocuments(String(caseId), {
        page,
        limit: PAGE_SIZE,
        ...(folderTab !== 'all' && { folder_type: folderTab }),
        ...(hashtagsParam && { hashtags: hashtagsParam }),
      } as any),
    enabled: !!caseId,
    select: (res) => {
      const d = (res.data as any)?.data ?? res.data;
      const itemsRaw: any[] = d?.items ?? d?.documents ?? d ?? [];
      const items: CaseDocument[] = itemsRaw.map((row) => ({
        document_id: row.document_id != null ? row.document_id : '',
        original_file_name:
          row.original_file_name ?? row.file_name ?? 'Untitled document',
        file_extension: row.file_extension ?? null,
        mime_type: row.mime_type ?? null,
        file_size_bytes:
          typeof row.file_size_bytes === 'number'
            ? row.file_size_bytes
            : typeof row.file_size_bytes === 'string' &&
              row.file_size_bytes.trim().length > 0 &&
              !Number.isNaN(Number(row.file_size_bytes))
            ? Number(row.file_size_bytes)
            : null,
        folder_type: row.folder_type ?? 'Case Files',
        document_type: row.document_type ?? 'Document',
        classification: row.classification ?? 'Public',
        approval_status: row.approval_status ?? 'Pending',
        page_count:
          typeof row.page_count === 'number' ? row.page_count : null,
        uploaded_by_name: row.uploaded_by_name ?? row.author ?? '—',
        created_at: row.created_at ?? new Date().toISOString(),
        is_favorite: !!row.is_favorite,
        can_view:
          typeof row.can_view === 'boolean' ? row.can_view : undefined,
        requires_access_request:
          typeof row.requires_access_request === 'boolean'
            ? row.requires_access_request
            : undefined,
      }));
      const pagination =
        d?.pagination ??
        ({
          page,
          limit: PAGE_SIZE,
          total: items.length,
          totalPages: Math.max(1, Math.ceil(items.length / PAGE_SIZE)),
        } as const);
      return { items, pagination };
    },
  });

  const favoriteMutation = useMutation({
    mutationFn: async (payload: {
      documentId: number | string;
      isFavorite: boolean;
    }) => {
      if (payload.isFavorite) {
        await documentService.removeFavorite(String(payload.documentId));
      } else {
        await documentService.addFavorite(String(payload.documentId));
      }
    },
    onSuccess: (_res, vars) => {
      toast.success(
        vars.isFavorite ? 'Removed from favorites' : 'Added to favorites'
      );
      queryClient.invalidateQueries({
        queryKey: ['case-documents', caseId],
      });
      queryClient.invalidateQueries({ queryKey: ['favorites'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'favorites'] });
    },
    onError: () => {
      toast.error('Failed to update favorites');
    },
  });

  const requestMutation = useMutation({
    mutationFn: async (payload: {
      documentId: number | string;
      reason: string;
      access_from_date: string;
      access_to_date: string;
    }) =>
      accessRequestService.submitRequest(String(payload.documentId), {
        reason: payload.reason,
        access_from_date: payload.access_from_date,
        access_to_date: payload.access_to_date,
      }),
    onSuccess: () => {
      toast.success('Access request submitted successfully');
      setRequestDoc(null);
    },
    onError: (err: any) => {
      if (err?.response?.status === 409) {
        toast.error('Access request already submitted for this document');
      } else {
        const msg =
          err?.response?.data?.message || 'Failed to submit access request';
        toast.error(msg);
      }
    },
  });

  const caseHashtagMutation = useMutation({
    mutationFn: async (payload: { hashtags: string[] }) =>
      caseService.updateCaseHashtags(String(caseId), payload.hashtags),
    onSuccess: (resp: any) => {
      const message =
        resp?.data?.message ?? 'Case hashtags updated successfully.';
      toast.success(message);
      setIsCaseHashtagModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['case', caseId] });
    },
    onError: (err: any) => {
      const msg =
        err?.response?.data?.message ?? 'Failed to update case hashtags.';
      toast.error(msg);
    },
  });

  const caseData = caseQuery.data;
  const docItems = docsQuery.data?.items ?? [];
  const filteredDocItems = useMemo(() => {
    const needle = debouncedFileNameSearch.trim().toLowerCase();
    if (!needle) return docItems;
    return docItems.filter((doc) => {
      const displayName = formatDocumentDisplayName({
        cmsNumber: caseData?.cms_number,
        originalFileName: doc.original_file_name,
        documentType: doc.document_type,
      });
      return displayName.toLowerCase().includes(needle);
    });
  }, [docItems, debouncedFileNameSearch, caseData?.cms_number]);
  const pagination = docsQuery.data?.pagination ?? {
    page,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  };

  const isLoadingCase = caseQuery.isLoading;
  const isLoadingDocs = docsQuery.isLoading || docsQuery.isFetching;

  useEffect(() => {
    const pendingTags = Array.isArray(caseData?.pending_hashtags)
      ? caseData?.pending_hashtags ?? []
      : [];
    const approvedTags = Array.isArray(caseData?.hashtags)
      ? caseData?.hashtags ?? []
      : [];
    const rejectedTagsRaw = Array.isArray(caseData?.rejected_hashtags)
      ? caseData?.rejected_hashtags ?? []
      : [];
    const rejectedTags = rejectedTagsRaw.filter(
      (tag) => !approvedTags.includes(tag)
    );
    const timeline = Array.isArray(caseData?.hashtag_timeline)
      ? caseData?.hashtag_timeline ?? []
      : [];
    setApprovedCaseHashtags(approvedTags);
    setRejectedCaseHashtags(rejectedTags);
    setCaseHashtagTimeline(timeline);
    setCaseHashtags(pendingTags);
  }, [
    caseData?.case_id,
    caseData?.hashtags,
    caseData?.pending_hashtags,
    caseData?.rejected_hashtags,
    caseData?.hashtag_timeline,
    caseData?.case_approval_status,
  ]);

  const handleDownload = (doc: CaseDocument) => {
    const isConfidential = doc.classification === 'Confidential';
    const canView =
      typeof doc.can_view === 'boolean'
        ? doc.can_view
        : isConfidential
        ? false
        : true;

    if (isConfidential && !canView && role === 'RRMS_USER') {
      toast.error("You don't have access to this document");
      return;
    }
    documentService
      .downloadDocument(
        String(doc.document_id),
        formatDocumentDisplayName({
          cmsNumber: caseData?.cms_number,
          originalFileName: doc.original_file_name,
          documentType: doc.document_type,
        })
      )
      .catch((err: any) => {
        toast.error(err?.message || 'Download failed');
      });
  };

  const handlePreview = (doc: CaseDocument) => {
    const isConfidential = doc.classification === 'Confidential';
    const canView =
      typeof doc.can_view === 'boolean'
        ? doc.can_view
        : isConfidential
        ? false
        : true;

    if (isConfidential && !canView && role === 'RRMS_USER') {
      toast.error("You don't have access to this document");
      return;
    }
    setPreviewDocId(doc.document_id);
  };

  const handleFavoriteToggle = (doc: CaseDocument) => {
    favoriteMutation.mutate({
      documentId: doc.document_id,
      isFavorite: !!doc.is_favorite,
    });
  };

  const handleOpenRequestAccess = (doc: CaseDocument) => {
    setRequestDoc(doc);
  };

  const handleUploadMore = () => {
    if (!caseData) return;
    navigate(ROUTES.UPLOAD, {
      state: {
        prefillCaseId: String(caseData.case_id),
        prefillCmsNumber: caseData.cms_number,
      },
    });
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f0f2f5',
        padding: '16px 12px',
        fontFamily: 'DM Sans, sans-serif',
      }}
    >
      <div
        style={{
          maxWidth: '100%',
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        {/* Top action bar */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <button
            type="button"
            onClick={handleBackNavigation}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: 500,
              color: '#64748b',
              cursor: 'pointer',
              fontFamily: 'inherit',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              transition: 'border-color 0.15s',
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.borderColor = '#94a3b8')
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.borderColor = '#e2e8f0')
            }
          >
            <ArrowLeft size={14} />
            Back to Cases
          </button>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              onClick={() => navigate(ROUTES.UPLOAD)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '9px 16px',
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 600,
                color: 'var(--color-primary)',
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'border-color 0.15s, background-color 0.15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#bfdbfe';
                e.currentTarget.style.background = '#f8fbff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e2e8f0';
                e.currentTarget.style.background = 'white';
              }}
            >
              <Upload size={14} />
              Upload Documents
            </button>

            <button
              type="button"
              onClick={() => setIsCaseHashtagModalOpen(true)}
              disabled={!caseData}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '9px 16px',
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 600,
                color: 'var(--color-primary)',
                cursor: caseData ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit',
                transition: 'border-color 0.15s, background-color 0.15s',
                opacity: caseData ? 1 : 0.6,
              }}
              onMouseEnter={(e) => {
                if (!caseData) return;
                e.currentTarget.style.borderColor = '#bfdbfe';
                e.currentTarget.style.background = '#f8fbff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e2e8f0';
                e.currentTarget.style.background = 'white';
              }}
            >
              <Hash size={14} />Hashtags
            </button>

            <button
              type="button"
              onClick={handleUploadMore}
              disabled={!caseData}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '9px 20px',
                background: 'var(--color-primary)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 600,
                color: 'white',
                cursor: caseData ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit',
                boxShadow: '0 2px 10px rgba(26,58,107,0.35)',
                transition: 'opacity 0.15s',
                opacity: caseData ? 1 : 0.6,
              }}
              onMouseEnter={(e) => {
                if (caseData) e.currentTarget.style.opacity = '0.9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = caseData ? '1' : '0.6';
              }}
            >
              <Upload size={14} />
              Upload More
            </button>
          </div>
        </div>

        {/* Case header card / loading / error */}
        {isLoadingCase ? (
          <div
            style={{
              borderRadius: '14px',
              border: '1px solid #e2e8f0',
              overflow: 'hidden',
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              background: 'white',
              padding: '24px',
              animation: 'skeleton-pulse 1.5s ease-in-out infinite',
            }}
          />
        ) : caseQuery.isError ? (
          <div
            style={{
              borderRadius: '10px',
              border: '1px solid var(--color-error)',
              background: 'var(--color-error-light)',
              padding: '14px 16px',
              fontSize: '13px',
              color: 'var(--color-error)',
            }}
          >
            Failed to load case details. Please try again.
          </div>
        ) : caseData ? (
          <div
            style={{
              borderRadius: '14px',
              border: '1px solid #e2e8f0',
              overflow: 'hidden',
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            }}
          >
            <div
              style={{
                background:
                  'linear-gradient(135deg, #1a3a6b 0%, #0d2244 100%)',
                padding: '24px 28px',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: '-40px',
                  right: '-40px',
                  width: '200px',
                  height: '200px',
                  borderRadius: '50%',
                  border: '40px solid rgba(255,255,255,0.04)',
                  pointerEvents: 'none',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  bottom: '-60px',
                  right: '80px',
                  width: '160px',
                  height: '160px',
                  borderRadius: '50%',
                  border: '30px solid rgba(200,151,58,0.07)',
                  pointerEvents: 'none',
                }}
              />

              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '3px 10px',
                  background: 'rgba(200,151,58,0.15)',
                  border: '1px solid rgba(200,151,58,0.35)',
                  borderRadius: '5px',
                  fontSize: '10px',
                  fontWeight: 700,
                  color: '#c8973a',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  marginBottom: '10px',
                }}
              >
                <FileText size={10} />
                Case Record
              </div>

              <h1
                style={{
                  margin: 0,
                  fontFamily: 'DM Mono, monospace',
                  fontSize: 'clamp(1.1rem, 2.5vw, 1.6rem)',
                  fontWeight: 600,
                  color: 'white',
                  letterSpacing: '0.04em',
                  lineHeight: 1.2,
                }}
              >
                {caseData.fir_number || caseData.cms_number}
              </h1>

              <p
                style={{
                  margin: '4px 0 0',
                  fontFamily: 'DM Mono, monospace',
                  fontSize: '11px',
                  color: 'rgba(255,255,255,0.35)',
                  letterSpacing: '0.06em',
                }}
              >
                REF: {caseData.rrms_ref_number}
              </p>
            </div>

            <div
              style={{
                background: 'white',
                borderTop: '1px solid #f1f5f9',
                padding: '16px 28px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px',
              }}
            >
              {(
                [
                  {
                    label: 'File No',
                    value: caseData.fir_number,
                    accent: true,
                  },
                  {
                    label: 'Year',
                    value:
                      typeof caseData.year === 'number'
                        ? String(caseData.year)
                        : '',
                  },
                  { label: 'Section / Sub-Section', value: caseData.hierarchy_path ?? '' },
                  { label: 'File Category', value: caseData.category ?? '' },
                  { label: 'Subject', value: caseData.subject ?? '' },
                  {
                    label: 'File Start',
                    value: caseData.file_start_date
                      ? String(caseData.file_start_date).slice(0, 10)
                      : '',
                  },
                  {
                    label: 'File End',
                    value: caseData.file_end_date
                      ? String(caseData.file_end_date).slice(0, 10)
                      : '',
                  },
                ] as { label: string; value?: string; accent?: boolean }[]
              ).filter(({ value }) => value).map(({ label, value, accent }) => (
                <div
                  key={label}
                  title={value || undefined}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '8px 14px',
                    background: accent ? '#eff6ff' : '#f8fafc',
                    border: `1px solid ${
                      accent ? '#bfdbfe' : '#e2e8f0'
                    }`,
                    borderRadius: '8px',
                    minWidth: '90px',
                  }}
                >
                  <span
                    style={{
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.07em',
                      color: '#94a3b8',
                      fontWeight: 600,
                      marginBottom: '3px',
                    }}
                  >
                    {label}
                  </span>
                  <span
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: accent
                        ? '#1d4ed8'
                        : 'var(--color-primary)',
                      maxWidth: '260px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {value || '—'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Stats row */}
        {(() => {
          const allDocs = docItems ?? [];
          const total = allDocs.length;

          return (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
                gap: '12px',
              }}
            >
              {[
                {
                  icon: <FileText size={18} color="#2563eb" />,
                  iconBg: '#eff6ff',
                  num: total,
                  label: 'Total Documents',
                },
              ].map(({ icon, iconBg, num, label }) => (
                <div
                  key={label}
                  style={{
                    background: 'white',
                    borderRadius: '12px',
                    padding: '16px 20px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                  }}
                >
                  <div
                    style={{
                      width: '42px',
                      height: '42px',
                      borderRadius: '10px',
                      background: iconBg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {icon}
                  </div>
                  <div>
                    <div
                      style={{
                        fontFamily: 'DM Mono, monospace',
                        fontSize: '22px',
                        fontWeight: 600,
                        color: '#0f172a',
                        lineHeight: 1,
                      }}
                    >
                      {num}
                    </div>
                    <div
                      style={{
                        fontSize: '11px',
                        color: '#94a3b8',
                        marginTop: '3px',
                        fontWeight: 500,
                      }}
                    >
                      {label}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        {/* Filter bar */}
        {/* <div
          style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '10px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}
        >
          <div style={{ flex: 1, position: 'relative' }}>
            <Hash
              size={14}
              style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#94a3b8',
                pointerEvents: 'none',
              }}
            />
            <input
              type="text"
              value={hashtagsInput}
              onChange={(e) => setHashtagsInput(e.target.value)}
              placeholder="Filter by hashtags — murder, 2024..."
              style={{
                width: '100%',
                paddingLeft: '30px',
                paddingRight: '12px',
                paddingTop: '8px',
                paddingBottom: '8px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '13px',
                fontFamily: 'inherit',
                background: '#f8fafc',
                outline: 'none',
                color: '#475569',
                boxSizing: 'border-box',
                transition: 'border-color 0.15s',
              }}
              onFocus={(e) =>
                (e.target.style.borderColor = 'var(--color-primary)')
              }
              onBlur={(e) => (e.target.style.borderColor = '#e2e8f0')}
            />
          </div>

          <div
            style={{
              width: '1px',
              height: '30px',
              background: '#e2e8f0',
              flexShrink: 0,
            }}
          />

          <div
            style={{
              display: 'flex',
              background: '#f1f5f9',
              borderRadius: '8px',
              padding: '3px',
              gap: '2px',
            }}
          >
            {(['all', 'Case Files', 'Correspondence'] as FolderTab[]).map(
              (tab) => {
                const label = tab === 'all' ? 'All' : tab;
                const isActive = folderTab === tab;
                return (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setFolderTab(tab)}
                    style={{
                      padding: '5px 14px',
                      background: isActive ? 'white' : 'transparent',
                      color: isActive
                        ? 'var(--color-primary)'
                        : '#64748b',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: isActive ? 600 : 500,
                      fontFamily: 'inherit',
                      boxShadow: isActive
                        ? '0 1px 3px rgba(0,0,0,0.08)'
                        : 'none',
                      transition: 'all 0.15s',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {label}
                  </button>
                );
              }
            )}
          </div>
        </div> */}

        {/* Documents section */}
        <div
          style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '6px 10px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}
        >
          <input
            type="text"
            value={fileNameSearch}
            onChange={(e) => setFileNameSearch(e.target.value)}
            placeholder="Search files by name..."
            style={{
              width: '100%',
              height: '30px',
              padding: '4px 10px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '12px',
              fontFamily: 'inherit',
              background: '#f8fafc',
              outline: 'none',
              color: '#475569',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {isLoadingDocs ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                style={{
                  background: 'white',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  borderLeft: '3px solid #e2e8f0',
                  padding: '16px 18px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                }}
              >
                <div
                  style={{
                    width: '44px',
                    height: '44px',
                    background: '#f1f5f9',
                    borderRadius: '10px',
                    flexShrink: 0,
                    animation: 'skeleton-pulse 1.5s ease-in-out infinite',
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      height: '14px',
                      background: '#f1f5f9',
                      borderRadius: '4px',
                      width: '55%',
                      marginBottom: '10px',
                      animation:
                        'skeleton-pulse 1.5s ease-in-out infinite',
                    }}
                  />
                  <div
                    style={{
                      height: '11px',
                      background: '#f1f5f9',
                      borderRadius: '4px',
                      width: '38%',
                      animation:
                        'skeleton-pulse 1.5s ease-in-out infinite',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : docsQuery.isError ? (
          <div
            style={{
              textAlign: 'center',
              padding: '40px 24px',
              borderRadius: '12px',
              border: '1px solid #fecaca',
              background: '#fef2f2',
              fontSize: '13px',
              color: '#b91c1c',
            }}
          >
            Failed to load documents. Please adjust filters or try again.
          </div>
        ) : filteredDocItems.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '56px 24px',
              border: '1px dashed #e2e8f0',
              borderRadius: '12px',
              background: 'white',
            }}
          >
            <div
              style={{
                width: '56px',
                height: '56px',
                margin: '0 auto 16px',
                background: '#f8fafc',
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <File size={26} color="#cbd5e1" />
            </div>
            <p
              style={{
                margin: 0,
                fontSize: '14px',
                fontWeight: 600,
                color: '#475569',
              }}
            >
              No documents found
            </p>
            <p
              style={{
                margin: '6px 0 0',
                fontSize: '13px',
                color: '#94a3b8',
              }}
            >
              {fileNameSearch.trim()
                ? 'No files match this file name search'
                : folderTab !== 'all' || hashtagsInput
                ? 'Try clearing your filters'
                : 'Upload documents using the button above'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <p
              style={{
                fontSize: '12px',
                fontWeight: 600,
                color: '#94a3b8',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                padding: '0 4px',
              }}
            >
              {filteredDocItems.length} document
              {filteredDocItems.length !== 1 ? 's' : ''}
            </p>

            {filteredDocItems.map((doc) => {
              const isConfidential = doc.classification === 'Confidential';
              const isRejected =
                doc.approval_status === 'Rejected' ||
                doc.approval_status === 'Denied';

              const ext = (doc.original_file_name || '')
                .split('.')
                .pop()
                ?.toLowerCase();
              const isImageExt = ext
                ? ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)
                : false;
              const isSheetExt = ext
                ? ['xlsx', 'xls', 'csv'].includes(ext)
                : false;
              const FileIcon = isImageExt
                ? Image
                : isSheetExt
                ? FileSpreadsheet
                : FileText;

              const isRestricted =
                isConfidential &&
                doc.can_view === false &&
                role === 'RRMS_USER';
              const canPreviewByType = canPreviewDocument({
                fileName: doc.original_file_name,
                fileExtension: doc.file_extension,
                mimeType: doc.mime_type,
              });
              const canDownloadByType = canDownloadDocument({
                fileName: doc.original_file_name,
                fileExtension: doc.file_extension,
                mimeType: doc.mime_type,
              });
              const hideDownloadWhilePdfOrImagePreview =
                previewDocId != null &&
                (previewContentKind === 'pdf' ||
                  previewContentKind === 'image') &&
                String(doc.document_id) === String(previewDocId);
              const displayFileName = formatDocumentDisplayName({
                cmsNumber: caseData?.cms_number,
                originalFileName: doc.original_file_name,
                documentType: doc.document_type,
              });

              return (
                <div
                  key={doc.document_id}
                  style={{
                    background: 'white',
                    border: '1px solid #e2e8f0',
                    borderLeft: `3px solid ${
                      isConfidential ? '#ef4444' : '#22c55e'
                    }`,
                    borderRadius: '12px',
                    padding: '16px 18px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    transition:
                      'box-shadow 0.15s, transform 0.15s',
                    cursor: 'default',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow =
                      '0 4px 12px rgba(0,0,0,0.08)';
                    e.currentTarget.style.transform =
                      'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow =
                      '0 1px 3px rgba(0,0,0,0.04)';
                    e.currentTarget.style.transform =
                      'translateY(0)';
                  }}
                >
                  <div
                    style={{
                      width: '44px',
                      height: '44px',
                      flexShrink: 0,
                      background: '#f1f5f9',
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <FileIcon size={20} color="#94a3b8" />
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        flexWrap: 'wrap',
                        marginBottom: '5px',
                      }}
                    >
                      {isRestricted || !canPreviewByType ? (
                        <span
                          style={{
                            fontSize: '14px',
                            fontWeight: 600,
                            color: '#475569',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                          }}
                          title={displayFileName}
                        >
                          {displayFileName}
                        </span>
                      ) : (
                        <span
                          onClick={() => handlePreview(doc)}
                          style={{
                            fontSize: '14px',
                            fontWeight: 600,
                            color: 'var(--color-primary)',
                            cursor: 'pointer',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                          }}
                          title={displayFileName}
                        >
                          {displayFileName}
                        </span>
                      )}

                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '3px',
                          padding: '2px 8px',
                          borderRadius: '999px',
                          fontSize: '11px',
                          fontWeight: 600,
                          flexShrink: 0,
                          background: isConfidential
                            ? 'rgba(239,68,68,0.08)'
                            : 'rgba(34,197,94,0.08)',
                          color: isConfidential
                            ? '#dc2626'
                            : '#16a34a',
                          border: `1px solid ${
                            isConfidential
                              ? 'rgba(239,68,68,0.2)'
                              : 'rgba(34,197,94,0.2)'
                          }`,
                        }}
                      >
                        {isConfidential ? (
                          <Lock size={9} />
                        ) : null}
                        {doc.classification}
                      </span>
                    </div>

                    <p
                      style={{
                        margin: 0,
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'var(--font-weight-medium)',
                        color: '#94a3b8',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        flexWrap: 'wrap',
                      }}
                    >
                      <span>{doc.folder_type}</span>
                      <span style={{ color: '#cbd5e1' }}>·</span>
                      <span>{doc.document_type}</span>
                      <span style={{ color: '#cbd5e1' }}>·</span>
                      <span>
                        Pages:{' '}
                        {typeof doc.page_count === 'number' &&
                        doc.page_count > 0
                          ? doc.page_count
                          : '—'}
                      </span>
                      <span style={{ color: '#cbd5e1' }}>·</span>
                      <span>Size: {formatFileSize(doc.file_size_bytes)}</span>
                      <span style={{ color: '#cbd5e1' }}>·</span>
                      {/* by {doc.uploaded_by_name || '—'} */}
                      <span>
                        Uploaded on{' '}
                        {doc.created_at
                          ? format(
                              new Date(doc.created_at),
                              'dd MMM yyyy, HH:mm'
                            )
                          : '—'}
                      </span>
                    </p>

                    {isRejected && doc.denial_reason && (
                      <p
                        style={{
                          marginTop: 4,
                          marginBottom: 0,
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 'var(--font-weight-medium)',
                          color: '#dc2626',
                        }}
                      >
                        Reason: {doc.denial_reason}
                      </p>
                    )}
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flexShrink: 0,
                    }}
                  >
                    {!isRestricted && (
                      <>
                        {canPreviewByType && (
                          <button
                            type="button"
                            onClick={() => handlePreview(doc)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px',
                              padding: '6px 12px',
                              background: 'white',
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px',
                              fontSize: '12px',
                              fontWeight: 500,
                              color: '#475569',
                              cursor: 'pointer',
                              fontFamily: 'inherit',
                              transition:
                                'border-color 0.15s, color 0.15s',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor =
                                'var(--color-primary)';
                              e.currentTarget.style.color =
                                'var(--color-primary)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor =
                                '#e2e8f0';
                              e.currentTarget.style.color =
                                '#475569';
                            }}
                          >
                            <Eye size={13} /> Preview
                          </button>
                        )}

                        {canDownloadByType && !hideDownloadWhilePdfOrImagePreview && (
                          <button
                            type="button"
                            onClick={() => handleDownload(doc)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px',
                              padding: '6px 12px',
                              background: 'white',
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px',
                              fontSize: '12px',
                              fontWeight: 500,
                              color: '#475569',
                              cursor: 'pointer',
                              fontFamily: 'inherit',
                              transition:
                                'border-color 0.15s, color 0.15s',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor =
                                'var(--color-primary)';
                              e.currentTarget.style.color =
                                'var(--color-primary)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor =
                                '#e2e8f0';
                              e.currentTarget.style.color =
                                '#475569';
                            }}
                          >
                            <Download size={13} /> Download
                          </button>
                        )}
                      </>
                    )}

                    <button
                      type="button"
                      onClick={() => handleFavoriteToggle(doc)}
                      title={
                        doc.is_favorite
                          ? 'Remove from favorites'
                          : 'Add to favorites'
                      }
                      style={{
                        width: '34px',
                        height: '34px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: doc.is_favorite
                          ? 'rgba(239,68,68,0.06)'
                          : 'white',
                        border: `1px solid ${
                          doc.is_favorite
                            ? 'rgba(239,68,68,0.25)'
                            : '#e2e8f0'
                        }`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                    >
                      <Heart
                        size={14}
                        fill={doc.is_favorite ? '#ef4444' : 'none'}
                        color={
                          doc.is_favorite ? '#ef4444' : '#94a3b8'
                        }
                      />
                    </button>

                    {role === 'RRMS_USER' &&
                      doc.requires_access_request === true && (
                        <button
                          type="button"
                          onClick={() => handleOpenRequestAccess(doc)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            padding: '6px 14px',
                            background: 'var(--color-primary)',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: 'white',
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                            boxShadow:
                              '0 2px 6px rgba(26,58,107,0.25)',
                            transition: 'opacity 0.15s',
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.opacity = '0.88')
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.opacity = '1')
                          }
                        >
                          <KeyRound size={13} /> Request Access
                        </button>
                      )}
                  </div>
                </div>
              );
            })}

            {pagination.totalPages > 1 && (
              <div
                style={{
                  marginTop: '12px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  borderTop: '1px solid #e2e8f0',
                  paddingTop: '12px',
                  fontSize: '13px',
                }}
              >
                <p style={{ margin: 0, color: '#94a3b8' }}>
                  Page {pagination.page} of {pagination.totalPages} (
                  {pagination.total} total)
                </p>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={pagination.page <= 1}
                    style={{
                      padding: '6px 14px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      background: 'white',
                      fontSize: '12px',
                      color: '#475569',
                      cursor:
                        pagination.page <= 1
                          ? 'not-allowed'
                          : 'pointer',
                      opacity: pagination.page <= 1 ? 0.5 : 1,
                    }}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setPage((p) =>
                        Math.min(pagination.totalPages || 1, p + 1)
                      )
                    }
                    disabled={
                      pagination.totalPages === 0 ||
                      pagination.page >= pagination.totalPages
                    }
                    style={{
                      padding: '6px 14px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      background: 'white',
                      fontSize: '12px',
                      color: '#475569',
                      cursor:
                        pagination.totalPages === 0 ||
                        pagination.page >= pagination.totalPages
                          ? 'not-allowed'
                          : 'pointer',
                      opacity:
                        pagination.totalPages === 0 ||
                        pagination.page >= pagination.totalPages
                          ? 0.5
                          : 1,
                    }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <DocumentPreviewModal
          documentId={previewDocId}
          onClose={() => {
            setPreviewDocId(null);
            setPreviewContentKind(null);
          }}
          onPreviewContentKind={setPreviewContentKind}
        />

        {requestDoc && (
          <RequestAccessModal
            document={requestDoc}
            onClose={() => setRequestDoc(null)}
            onSubmit={(payload) =>
              requestMutation.mutate({
                documentId: requestDoc.document_id,
                reason: payload.reason,
                access_from_date: payload.access_from_date,
                access_to_date: payload.access_to_date,
              })
            }
            submitting={requestMutation.isPending}
          />
        )}

        {isCaseHashtagModalOpen && (
          <CaseHashtagModal
            tags={caseHashtags}
            approvedTags={approvedCaseHashtags}
            rejectedTags={rejectedCaseHashtags}
            statusTags={caseHashtagTimeline}
            onChange={setCaseHashtags}
            onClose={() => setIsCaseHashtagModalOpen(false)}
            onSubmit={() =>
              caseHashtagMutation.mutate({
                hashtags: caseHashtags,
              })
            }
            submitting={caseHashtagMutation.isPending}
          />
        )}
      </div>
    </div>
  );
};

interface CaseHashtagModalProps {
  tags: string[];
  approvedTags: string[];
  rejectedTags: string[];
  statusTags: Array<{ tag: string; status: 'Approved' | 'Rejected' | 'Pending' }>;
  onChange: (next: string[]) => void;
  onClose: () => void;
  onSubmit: () => void;
  submitting: boolean;
}

const CaseHashtagModal: React.FC<CaseHashtagModalProps> = ({
  tags,
  approvedTags,
  rejectedTags,
  statusTags,
  onChange,
  onClose,
  onSubmit,
  submitting,
}) => (
  <>
    <div
      role="presentation"
      onClick={onClose}
      className="fixed inset-0 z-40 bg-black/50"
    />
    <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-xl -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white p-5 shadow-xl">
      <h2 className="mb-1 text-md font-semibold text-[var(--color-gray-900)]">
        Add Case Hashtags
      </h2>
      <p className="mb-3 text-[15px] text-[var(--color-gray-600)]">
        Add up to 5 hashtags for this case.
      </p>

      <HashtagInput
        value={tags}
        readonlyTags={approvedTags}
        rejectedTags={rejectedTags}
        statusTags={statusTags}
        onChange={onChange}
        maxTags={5}
        placeholder="Add keywords like case, district, year..."
      />

      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-[var(--color-gray-200)] px-3 py-1.5 text-[15px] text-[var(--color-gray-700)] hover:bg-[var(--color-gray-50)]"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={submitting}
          onClick={onSubmit}
          className="rounded-md bg-[var(--color-primary)] px-3 py-1.5 text-[15px] font-medium text-white hover:bg-[var(--color-primary-light)] disabled:opacity-60"
        >
          {submitting ? 'Submitting...' : 'Submit'}
        </button>
      </div>
    </div>
  </>
);

interface RequestAccessModalProps {
  document: CaseDocument;
  onClose: () => void;
  onSubmit: (payload: {
    reason: string;
    access_from_date: string;
    access_to_date: string;
  }) => void;
  submitting: boolean;
}

const RequestAccessModal: React.FC<RequestAccessModalProps> = ({
  document,
  onClose,
  onSubmit,
  submitting,
}) => {
  const today = new Date().toISOString().slice(0, 10);
  const defaultTo = new Date();
  defaultTo.setFullYear(defaultTo.getFullYear() + 1);
  const defaultToStr = defaultTo.toISOString().slice(0, 10);

  const [reason, setReason] = useState('');
  const [accessFromDate, setAccessFromDate] = useState(today);
  const [accessToDate, setAccessToDate] = useState(defaultToStr);

  const handleConfirm = () => {
    if (reason.trim().length < 10) {
      toast.error('Reason must be at least 10 characters');
      return;
    }
    if (!accessFromDate || !accessToDate) {
      toast.error('Please select Access From date and Access To date');
      return;
    }
    if (accessToDate < accessFromDate) {
      toast.error('Access To date must be on or after Access From date');
      return;
    }
    onSubmit({
      reason: reason.trim(),
      access_from_date: accessFromDate,
      access_to_date: accessToDate,
    });
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <>
      <div
        role="presentation"
        onClick={onClose}
        className="fixed inset-0 z-40 bg-black/50"
      />
      <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white p-5 shadow-xl">
        <h2 className="mb-1 text-md font-semibold text-[var(--color-gray-900)]">
          Request Access
        </h2>
        <p className="mb-3 text-[15px] text-[var(--color-gray-600)]">
          Request access to this confidential document.
        </p>

        <p className="mb-3 text-[15px] text-[var(--color-gray-600)]">
          The request will go to your division&apos;s content manager.
        </p>

          <div className="mb-4 rounded-md border border-[var(--color-gray-200)] bg-[var(--color-gray-50)] px-3 py-2 text-[15px]">
          <p className="truncate font-semibold text-[var(--color-gray-800)]">
            {document.original_file_name}
          </p>
          <p className="mt-1 text-[var(--color-gray-600)]">
            {document.folder_type} · {document.document_type}
          </p>
        </div>

        <label className="mb-1 block text-[15px] font-medium text-[var(--color-gray-700)]">
          Access From Date
        </label>
        <input
          type="date"
          value={accessFromDate}
          onChange={(e) => setAccessFromDate(e.target.value)}
          min={today}
          className="mb-3 w-full rounded-md border border-[var(--color-gray-300)] px-2 py-1.5 text-xs"
        />

        <label className="mb-1 block text-[15px] font-medium text-[var(--color-gray-700)]">
          Access To Date
        </label>
        <input
          type="date"
          value={accessToDate}
          onChange={(e) => setAccessToDate(e.target.value)}
          min={accessFromDate}
          className="mb-3 w-full rounded-md border border-[var(--color-gray-300)] px-2 py-1.5 text-xs"
        />

        <label className="mb-1 block text-[15px] font-medium text-[var(--color-gray-700)]">
          Reason for Access
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          className="mb-3 w-full rounded-md border border-[var(--color-gray-300)] px-2 py-1.5 text-sm"
          placeholder="Explain why you need access (min 10 characters)..."
        />

        <div className="flex items-center justify-between text-[10px] text-[var(--color-gray-400)]">
          <span>{reason.length}/500</span>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-[var(--color-gray-200)] px-3 py-1.5 text-[15px] text-[var(--color-gray-700)] hover:bg-[var(--color-gray-50)]"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={handleConfirm}
            className="rounded-md bg-[var(--color-primary)] px-3 py-1.5 text-[15px] font-medium text-white hover:bg-[var(--color-primary-light)] disabled:opacity-60"
          >
            {submitting ? 'Submitting...' : 'Submit Request'}
          </button>
        </div>
      </div>
    </>
  );
};

export default CaseDetailPage;
